// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook that animates a number from 0 to a target value.
 *
 * The animation takes a minimum duration (default 3 seconds) to complete,
 * providing a smooth counting effect that makes data processing feel more
 * dynamic and engaging.
 *
 * @param targetValue - The final value to animate to
 * @param isReady - Whether the data is ready to start animating
 * @param minDurationMs - Minimum duration for the animation in milliseconds (default: 3000)
 * @returns The current animated value
 */
export function useAnimatedCount(
  targetValue: number,
  isReady: boolean,
  minDurationMs = 1000
): number {
  const [animatedValue, setAnimatedValue] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const previousTargetRef = useRef<number>(0);

  useEffect(() => {
    // Reset when target value changes (new data load)
    if (targetValue !== previousTargetRef.current) {
      previousTargetRef.current = targetValue;
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      setAnimatedValue(0);
      startTimeRef.current = null;
    }

    if (!isReady || targetValue === 0) {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      setAnimatedValue(0);
      startTimeRef.current = null;
      return;
    }

    // Start animation when data becomes ready
    if (startTimeRef.current === null) {
      startTimeRef.current = Date.now();
      setAnimatedValue(0);
    }

    const animate = () => {
      if (startTimeRef.current === null) {
        return;
      }

      const now = Date.now();
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / minDurationMs, 1);

      // Use a mostly linear easing with subtle smoothness
      const easedProgress = Math.pow(progress, 1.2);

      const currentValue = Math.floor(targetValue * easedProgress);
      setAnimatedValue(currentValue);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Ensure we end exactly at the target value
        setAnimatedValue(targetValue);
        animationFrameRef.current = null;
      }
    };

    // Start animation if not already running
    if (animationFrameRef.current === null) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [targetValue, isReady, minDurationMs]);

  return animatedValue;
}
